import { useEffect, useState } from "react";
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

/**
 * AI Hero Panel — sits under the mascot on Dashboard.
 *
 * Two states:
 *   (a) Income/debt not set yet → 2-field inline form to unlock personalised advice.
 *   (b) Set → render DTI ratio + health label + "Ask Hero Coach about my debt" CTA
 *       that opens the chat overlay (re-uses the existing coach-launcher button).
 */
function dtiLabel(pct) {
  if (pct < 20) return { text: "Excellent", tone: "ok",   icon: TrendingDown };
  if (pct < 36) return { text: "Healthy",   tone: "ok",   icon: TrendingDown };
  if (pct < 43) return { text: "Watch",     tone: "warn", icon: AlertTriangle };
  return         { text: "Stretched",       tone: "bad",  icon: TrendingUp };
}

export default function AIHeroPanel() {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [income, setIncome] = useState("");
  const [debt, setDebt] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/account")
      .then((r) => setAccount(r.data))
      .catch(() => { /* fall back to setup state */ })
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const inc = parseFloat(income);
    const dbt = parseFloat(debt);
    if (!inc || inc < 1) {
      toast.error("Enter your monthly income.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch("/account", {
        monthly_income: inc,
        monthly_debt: isNaN(dbt) ? 0 : Math.max(0, dbt),
      });
      setAccount(r.data);
      toast.success("Hero Coach unlocked.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const openCoach = () => document.querySelector('[data-testid="coach-launcher"]')?.click();

  if (loading) {
    return (
      <div className="mx-4 mt-2 rounded-3xl border border-[#1B4238] bg-[#0A2320] px-4 py-5 flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-[#22E07A]" />
      </div>
    );
  }

  const income$ = account?.monthly_income;
  const debt$ = account?.monthly_debt;
  const setupNeeded = !income$ || income$ < 1;
  const tier = (account?.tier || "recruit").toLowerCase();
  const isPaid = ["guardian","legend","goat"].includes(tier);

  if (setupNeeded) {
    return (
      <section
        className="mx-4 mt-2 rounded-3xl border border-[#1B4238] bg-gradient-to-br from-[#0E2924] to-[#072018] p-4 shadow-lg"
        data-testid="ai-hero-panel-setup"
      >
        <div className="flex items-start gap-2.5 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[#22E07A]/15 border border-[#22E07A]/40 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4.5 h-4.5 text-[#22E07A]" strokeWidth={2.6} />
          </div>
          <div>
            <p className="text-white font-extrabold text-[15px] leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Hero Coach is ready
            </p>
            <p className="text-[12px] text-[#94B0A6] mt-0.5">
              Tell me what comes in and what goes out each month, and I&apos;ll start coaching you on your real numbers.
            </p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#7B948B] font-bold mb-1">Monthly income</p>
              <Input
                inputMode="decimal"
                placeholder="3,500"
                value={income}
                onChange={(e) => setIncome(e.target.value.replace(/[^0-9.]/g, ""))}
                className="h-10 bg-[#0A2320] border-[#1B4238] text-white"
                data-testid="ai-input-income"
              />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#7B948B] font-bold mb-1">Monthly debt payments</p>
              <Input
                inputMode="decimal"
                placeholder="0"
                value={debt}
                onChange={(e) => setDebt(e.target.value.replace(/[^0-9.]/g, ""))}
                className="h-10 bg-[#0A2320] border-[#1B4238] text-white"
                data-testid="ai-input-debt"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={saving || !income}
            className="w-full h-10 rounded-2xl bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] font-extrabold uppercase tracking-wider text-xs gap-1.5"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="ai-save-financials"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Sparkles className="w-3.5 h-3.5" /> Unlock Hero Coach</>)}
          </Button>
        </form>
        <div className="mt-3 pt-3 border-t border-[#1B4238] text-[11px] text-[#94B0A6] leading-snug">
          <p className="font-bold text-[#C7DAD3] mb-1">What Hero Coach can do</p>
          <ul className="space-y-0.5 list-disc pl-4">
            <li><span className="text-[#C7DAD3]">Free (Recruit):</span> general budgeting & savings questions</li>
            <li><span className="text-emerald-300">Paid (Guardian/Legend/GOAT):</span> personalised advice based on your real numbers + persistent memory across chats</li>
          </ul>
        </div>
      </section>
    );
  }

  const pct = debt$ != null && income$ ? (debt$ / income$) * 100 : 0;
  const label = dtiLabel(pct);
  const Icon = label.icon;
  const toneClass =
    label.tone === "ok"   ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
    label.tone === "warn" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" :
                            "bg-red-500/15 text-red-300 border-red-500/30";

  return (
    <section
      className="mx-4 mt-2 rounded-3xl border border-[#1B4238] bg-gradient-to-br from-[#0E2924] to-[#072018] p-4 shadow-lg"
      data-testid="ai-hero-panel"
    >
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-xl bg-[#22E07A]/15 border border-[#22E07A]/40 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4.5 h-4.5 text-[#22E07A]" strokeWidth={2.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-white font-extrabold text-[15px] leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Hero Coach is online
            </p>
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${toneClass}`}>
              <Icon className="w-3 h-3" strokeWidth={2.6} /> {label.text}
            </span>
          </div>
          <p className="text-[12px] text-[#94B0A6] mt-0.5">
            DTI <span className="text-white font-bold">{pct.toFixed(1)}%</span>
            <span className="text-[#7B948B]"> · ${Number(income$).toFixed(0)} in · ${Number(debt$ || 0).toFixed(0)} out</span>
          </p>
        </div>
      </div>
      <Button
        onClick={openCoach}
        className="mt-3 w-full h-10 rounded-2xl bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] font-extrabold uppercase tracking-wider text-xs inline-flex items-center justify-center gap-1.5"
        style={{ fontFamily: "Outfit, sans-serif" }}
        data-testid="ai-ask-coach"
      >
        Ask about my finances <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.8} />
      </Button>
      <div className="mt-3 pt-3 border-t border-[#1B4238] text-[11px] text-[#94B0A6] leading-snug">
        <p className="font-bold text-[#C7DAD3] mb-1">What Hero Coach can do</p>
        <ul className="space-y-0.5 list-disc pl-4">
          <li><span className="text-[#C7DAD3]">Free (Recruit):</span> general budgeting & savings questions</li>
          <li><span className="text-emerald-300">Paid (Guardian/Legend/GOAT):</span> personalised advice based on your real numbers, debt payoff plans, &quot;what if&quot; scenarios, persistent memory across chats</li>
        </ul>
        {!isPaid && (
          <button
            type="button"
            onClick={() => window.location.assign("/upgrade")}
            className="mt-2 text-[11px] font-bold uppercase tracking-wider text-amber-300 hover:text-amber-200"
          >
            Upgrade for personalised coaching →
          </button>
        )}
      </div>
    </section>
  );
}
